import { OrganizationChart } from "@features/organization/OrganizationChart";
import { type Person as OrganizationPerson } from "@features/organization/OrganizationPage";
import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Card, Stack, Typography } from "@mui/material";
import { useAuth } from "@features/auth/context/AuthContext";
import { apiClient } from "@lib/api/apiClient";
import type { ApiResponse } from "@lib/api/api-types";
import { hasPermission } from "@web/config/permissions";

export function salesOrganizationPeople(people: OrganizationPerson[]) {
  const byId = new Map(people.map((person) => [person.id, person]));
  const included = new Set<number>();
  const reports = new Map<number, OrganizationPerson[]>();
  for (const person of people)
    if (person.managerId)
      reports.set(person.managerId, [
        ...(reports.get(person.managerId) || []),
        person,
      ]);
  const titles = new Set([
    "sales director",
    "sales manager",
    "sales consultant",
  ]);
  const queue = people.filter(
    (person) =>
      person.department?.trim().toLowerCase() === "sales" ||
      titles.has(person.designation?.trim().toLowerCase() || ""),
  );
  while (queue.length) {
    const person = queue.pop()!;
    if (included.has(person.id)) continue;
    included.add(person.id);
    queue.push(...(reports.get(person.id) || []));
  }
  for (const id of [...included]) {
    let current = byId.get(id);
    const visited = new Set<number>();
    while (current?.managerId && !visited.has(current.managerId)) {
      visited.add(current.managerId);
      included.add(current.managerId);
      current = byId.get(current.managerId);
    }
  }
  return people.filter((person) => included.has(person.id));
}
export function SalesReportingPage() {
  const { user } = useAuth();
  const allowed = Boolean(
    user &&
    user.permissions.some(
      (p) => p === "leads:view_team" || p === "leads:view_all",
    ) &&
    hasPermission(user!.role, "leads:view", user!.permissions),
  );
  return allowed ? (
    <SalesStructure />
  ) : (
    <Alert severity="error">
      Team or organization lead visibility permission required.
    </Alert>
  );
}
function SalesStructure() {
  const structure = useQuery({
    queryKey: ["sales-structure"],
    queryFn: async () =>
      (
        await apiClient.get<ApiResponse<{ people: OrganizationPerson[] }>>(
          "/sales/reporting/structure",
        )
      ).data.data,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
      >
        <Typography variant="h4">Sales Structure</Typography>
        <Button
          disabled={structure.isFetching}
          onClick={() => void structure.refetch()}
        >
          Refresh structure
        </Button>
      </Stack>
      <Typography color="text.secondary">
        Sales staff and their organizational reporting lines.
      </Typography>
      {structure.isLoading && (
        <Typography role="status">Loading structure…</Typography>
      )}
      {structure.error && (
        <Alert severity="error">{structure.error.message}</Alert>
      )}
      {structure.data && !structure.error && (
        <Card sx={{ p: 2 }}>
          <OrganizationChart
            title="Sales Structure"
            defaultExpanded
            directorStaffOnRight
            people={salesOrganizationPeople(structure.data.people)}
          />
        </Card>
      )}
    </Stack>
  );
}
